import { useEffect } from "react";
import { Link } from "react-router-dom";
import { GlassCard, PageHeader } from "@/components/shared";

// Canonical production support/contact address (see backend .env.example /
// ADMIN_EMAIL). These pages are drafted, factual templates covering the
// standard sections a payments-processing product needs public (Razorpay
// onboarding review, and basic user-facing transparency) -- they are not a
// substitute for review by a qualified professional before relying on them
// for legal/regulatory compliance (see final report, Section 10).
const SUPPORT_EMAIL = "vyomluvstudio@gmail.com";
const LAST_UPDATED = "16 September 2026";

function useDocumentTitle(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [title]);
}

function LegalShell({ eyebrow, title, children }) {
  return (
    <div className="vyom-bg min-h-screen py-10 px-4 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="text-sm text-slate-400 hover:text-slate-200 transition-colors">
          ← Back to Vyom Studio
        </Link>
        <div className="mt-4">
          <PageHeader eyebrow={eyebrow} title={title} subtitle={`Last updated: ${LAST_UPDATED}`} />
        </div>
        <GlassCard className="p-6 sm:p-8 mt-6 space-y-6 text-sm leading-relaxed text-slate-300">
          {children}
        </GlassCard>
      </div>
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <section>
      <h2 className="text-base font-semibold text-slate-100 mb-2">{heading}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  useDocumentTitle("Privacy Policy — Vyom Studio");
  return (
    <LegalShell eyebrow="Legal" title="Privacy Policy">
      <Section heading="1. What this covers">
        <p>
          This policy explains what information Vyom Studio ("Vyom", "we", "us") collects when
          you use the app, why we collect it, and the choices you have.
        </p>
      </Section>
      <Section heading="2. Information we collect">
        <p>
          Account information: your name, email address, and authentication identifiers,
          handled via our authentication provider (Clerk) or, for legacy accounts, a securely
          hashed password.
        </p>
        <p>
          Content you create: prompts, scripts, images, audio, and video you generate or upload
          while using Vyom, stored so you can access your own projects.
        </p>
        <p>
          Payment information: when you purchase a credit pack, payment is processed by
          Razorpay. Vyom never receives or stores your card, UPI, or bank details — only the
          order status, amount, and a payment reference id needed to credit your account.
        </p>
        <p>
          Usage information: basic technical logs (timestamps, error reports, rate-limit
          counters) used to keep the service reliable and secure.
        </p>
      </Section>
      <Section heading="3. How we use your information">
        <p>
          To provide and operate the service (generating and storing your content, tracking
          your credit balance); to process payments and prevent fraud or abuse; to maintain
          security (rate limiting, detecting suspicious activity); and to communicate
          service-related notices to you.
        </p>
        <p>
          We do not sell your personal information, and we do not use your generated content
          to train third-party models without your consent.
        </p>
      </Section>
      <Section heading="4. Third parties we work with">
        <p>
          Clerk (authentication), Razorpay (payment processing), and our cloud hosting and
          database providers. Each processes data only as needed to provide their part of the
          service.
        </p>
      </Section>
      <Section heading="5. Data retention">
        <p>
          We retain your account and project data for as long as your account is active. If
          you delete a project, its associated files are removed from active storage. You may
          request full account deletion by contacting us below.
        </p>
      </Section>
      <Section heading="6. Your rights">
        <p>
          You can request a copy of your personal data, request corrections, or request
          deletion of your account and associated data, subject to what we must legally retain
          (for example, payment records for tax and accounting purposes).
        </p>
      </Section>
      <Section heading="7. Cookies">
        <p>
          We use only the minimum cookies/local storage needed to keep you signed in and
          remember basic preferences. We do not use third-party advertising trackers.
        </p>
      </Section>
      <Section heading="8. Contact">
        <p>
          Questions about this policy or your data can be sent to{" "}
          <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}

export function TermsOfService() {
  useDocumentTitle("Terms of Service — Vyom Studio");
  return (
    <LegalShell eyebrow="Legal" title="Terms of Service">
      <Section heading="1. Acceptance">
        <p>
          By creating an account or using Vyom Studio, you agree to these Terms. If you do not
          agree, please do not use the service.
        </p>
      </Section>
      <Section heading="2. The service">
        <p>
          Vyom Studio is an AI-assisted video creation tool. Features, generation quality, and
          available AI providers may change as the product evolves. Some features may be in
          preview and could be limited, paused, or adjusted without prior notice.
        </p>
      </Section>
      <Section heading="3. Accounts">
        <p>
          You are responsible for keeping your account credentials secure and for all activity
          under your account. You must be legally able to enter into a contract in your
          jurisdiction to use Vyom.
        </p>
      </Section>
      <Section heading="4. Credits">
        <p>
          Vyom uses a prepaid credit system. Credits are a virtual unit of access to generation
          features within Vyom Studio only — they are not currency, a deposit, a financial
          instrument, or redeemable for cash, and cannot be transferred between accounts or
          exchanged with other users.
        </p>
        <p>
          Current credit packs and pricing are shown in-app at time of purchase and may change
          for future purchases; credits already granted to your account are unaffected by later
          pricing changes.
        </p>
      </Section>
      <Section heading="5. Acceptable use">
        <p>
          You agree not to use Vyom to generate content that is illegal, infringes another
          person's rights, depicts real identifiable people without their consent, sexualizes
          minors, or is intended to harass, defraud, or deceive. We may suspend or terminate
          accounts that violate this section.
        </p>
      </Section>
      <Section heading="6. Your content">
        <p>
          You retain ownership of the prompts you write and the output you generate using
          Vyom, subject to the acceptable-use restrictions above and to the underlying rights
          of any material you upload. You are responsible for having the rights to anything you
          upload as source material.
        </p>
      </Section>
      <Section heading="7. Payments and refunds">
        <p>
          Payments are processed by Razorpay. See our{" "}
          <Link className="underline" to="/refund-policy">
            Refund Policy
          </Link>{" "}
          for how refunds are handled.
        </p>
      </Section>
      <Section heading="8. Availability and liability">
        <p>
          Vyom is provided "as is." We work to keep the service reliable but do not guarantee
          uninterrupted availability. To the maximum extent permitted by law, Vyom is not
          liable for indirect or consequential damages arising from use of the service.
        </p>
      </Section>
      <Section heading="9. Changes">
        <p>
          We may update these Terms from time to time. Continued use of Vyom after an update
          means you accept the revised Terms.
        </p>
      </Section>
      <Section heading="10. Governing law">
        <p>These Terms are governed by the laws of India, without regard to conflict-of-law principles.</p>
      </Section>
      <Section heading="11. Contact">
        <p>
          <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </Section>
    </LegalShell>
  );
}

export function RefundPolicy() {
  useDocumentTitle("Refund Policy — Vyom Studio");
  return (
    <LegalShell eyebrow="Legal" title="Refund & Cancellation Policy">
      <Section heading="1. Credits are delivered instantly">
        <p>
          When a credit pack purchase succeeds, credits are added to your account immediately.
          Because credits are a digital good delivered at the time of purchase, purchases are
          generally non-refundable once credits have been added to your account.
        </p>
      </Section>
      <Section heading="2. When a refund does apply">
        <p>
          If a payment is deducted from your account but credits were not delivered due to a
          technical failure on our side, contact us with your payment reference and we will
          either deliver the missing credits or refund the payment, typically within 7 business
          days of verifying the issue.
        </p>
        <p>
          If you believe you were charged in error or notice a duplicate charge for the same
          order, contact us immediately so we can investigate with Razorpay.
        </p>
      </Section>
      <Section heading="3. How to request a refund">
        <p>
          Email{" "}
          <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>{" "}
          with your registered email address, the approximate date of the transaction, and the
          payment reference id shown in your Razorpay receipt.
        </p>
      </Section>
      <Section heading="4. Processing time">
        <p>
          Approved refunds are returned to your original payment method via Razorpay. Depending
          on your bank or card network, this can take 5-10 business days to reflect after we
          initiate it.
        </p>
      </Section>
    </LegalShell>
  );
}

export function ContactUs() {
  useDocumentTitle("Contact — Vyom Studio");
  return (
    <LegalShell eyebrow="Support" title="Contact Us">
      <Section heading="Support">
        <p>
          For account, billing, or technical support, email{" "}
          <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          . We aim to respond within 2 business days.
        </p>
      </Section>
      <Section heading="Business">
        <p>Vyom Studio is an independent product. For business or partnership inquiries, use the same address above.</p>
      </Section>
    </LegalShell>
  );
}

export function AboutVyom() {
  useDocumentTitle("About — Vyom Studio");
  return (
    <LegalShell eyebrow="About" title="About Vyom Studio">
      <Section heading="What Vyom Studio is">
        <p>
          Vyom Studio is an AI-powered cinematic video production platform. It turns a written
          idea into a directed, multi-scene AI video -- keeping characters, voice, and visual
          style consistent from the first shot to the last.
        </p>
      </Section>
      <Section heading="How it works">
        <p>
          Vyom is AI-directed: a prompt becomes a shot-by-shot cinematic sequence, from story to
          scene to final render. Every generation is saved to a project you can revisit, re-edit,
          and export. Vyom uses a simple prepaid credit system -- you pay only for what you
          generate, with no subscription lock-in.
        </p>
      </Section>
      <Section heading="Who runs it">
        <p>Vyom Studio is an independent product.</p>
      </Section>
      <Section heading="Contact">
        <p>
          <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </Section>
    </LegalShell>
  );
}
